import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { baseAPI } from '../config';
import { NumericFormat } from 'react-number-format';
import MainLayout from '../layouts/MainLayout';
import Pagination from '../components/Pagination';
import UserSelect from '../components/userSelectList';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/themes/material_blue.css';
import moment from 'moment';

const RecoveredAmountList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ current_page: 1, total_pages: 1, total_items: 0 });

  const userToken = localStorage.getItem('token');

  const currentPage = parseInt(searchParams.get('page') || '1');
  const selectedUser = searchParams.get('cliente') || '';
  const selectedType = searchParams.get('type') || '';
  const startDate = searchParams.get('startDate') || '';
  const endDate = searchParams.get('endDate') || '';

  const updateParam = useCallback(
    (key, value) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        if (value) {
          next.set(key, value);
        } else {
          next.delete(key);
        }
        // Reset page when filters change
        if (key !== 'page') {
          next.delete('page');
        }
        return next;
      });
    },
    [setSearchParams]
  );

  useEffect(() => {
    const fetchRecoveredAmounts = async () => {
      setLoading(true);
      try {
        const params = { page: currentPage, limit: 100 };
        if (selectedUser) params.user_id = selectedUser;
        if (selectedType) params.type = selectedType;
        if (startDate) params.startDate = new Date(startDate).toISOString();
        if (endDate) params.endDate = new Date(endDate).toISOString();

        const response = await axios.get(`${baseAPI}/expedients/recovered-amounts`, {
          headers: { Authorization: `Bearer ${userToken}` },
          params,
        });

        setItems(response.data.items);
        setPagination(response.data.pagination);
      } catch (error) {
        console.error('Error fetching recovered amounts', error);
        toast.error('Error al cargar los importes recuperados.');
      } finally {
        setLoading(false);
      }
    };

    fetchRecoveredAmounts();
  }, [currentPage, selectedUser, selectedType, startDate, endDate, userToken]);

  return (
    <MainLayout>
      <div className='sm:flex sm:justify-between sm:items-center mb-6'>
        <h1 className='text-2xl font-semibold text-slate-900'>Importes recuperados</h1>
        {!loading && (
          <span className='text-sm text-gray-500'>{pagination.total_items} importes recuperados</span>
        )}
      </div>

      {/* Filters */}
      <div className='bg-white shadow-sm rounded-lg border border-gray-200 p-6 mb-6'>
        <div className='grid grid-cols-1 md:grid-cols-4 gap-4 items-end'>
          <div className='[&>div]:mb-0 [&>div>label]:hidden'>
            <label className='block text-sm font-medium text-gray-700 mb-2'>Cliente</label>
            <UserSelect selectedUser={selectedUser} onUserChange={value => updateParam('cliente', value)} />
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-700 mb-2'>Tipo</label>
            <select
              className='bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5'
              value={selectedType}
              onChange={e => updateParam('type', e.target.value)}
            >
              <option value=''>Todos</option>
              <option value='extrajudicial_payment'>Pago extrajudicial</option>
              <option value='court_ordered_payment'>Pago judicial</option>
              <option value='debt_reduction'>Quita</option>
              <option value='bond'>Fianza</option>
              <option value='credit'>Abono</option>
              <option value='cancellation'>Cancelación</option>
            </select>
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-700 mb-2'>Fecha inicio</label>
            <Flatpickr
              value={startDate || undefined}
              onChange={([date]) => updateParam('startDate', date ? moment(date).format('YYYY-MM-DD') : '')}
              placeholder='Fecha inicio'
              className='block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-lg'
              options={{
                dateFormat: 'd/m/Y',
                maxDate: endDate || undefined,
              }}
            />
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-700 mb-2'>Fecha fin</label>
            <Flatpickr
              value={endDate || undefined}
              onChange={([date]) => updateParam('endDate', date ? moment(date).format('YYYY-MM-DD') : '')}
              placeholder='Fecha fin'
              className='block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-lg'
              options={{
                dateFormat: 'd/m/Y',
                minDate: startDate || undefined,
              }}
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className='bg-white shadow-sm rounded-lg border border-gray-200'>
        <div className='overflow-x-auto'>
          {loading ? (
            <p className='text-center text-gray-400 py-6'>Cargando...</p>
          ) : items.length === 0 ? (
            <p className='text-center text-gray-400 py-6'>No hay importes recuperados en el rango seleccionado.</p>
          ) : (
            <table className='table-auto w-full text-left'>
              <thead className='text-xs font-semibold uppercase text-gray-600 bg-gray-100'>
                <tr>
                  <th className='px-3 py-2'>ID Expediente</th>
                  <th className='px-3 py-2'>Concepto</th>
                  <th className='px-3 py-2'>Cliente</th>
                  <th className='px-3 py-2'>Tipo</th>
                  <th className='px-3 py-2'>Fecha</th>
                  <th className='px-3 py-2'>Importe</th>
                  <th className='px-3 py-2'>Referencia contrato</th>
                </tr>
              </thead>
              <tbody className='text-sm divide-y divide-gray-200'>
                {items.map((item, index) => (
                  <tr key={`${item.expedient_id}-${index}`}>
                    <td className='px-3 py-2 whitespace-nowrap'>
                      <Link to={`/view/${item.expedient_id}`} className='text-blue-600 hover:underline'>
                        {item.expedient_id}
                      </Link>
                    </td>
                    <td className='px-3 py-2 whitespace-nowrap text-gray-800'>{item.activity_title}</td>
                    <td className='px-3 py-2 whitespace-nowrap text-gray-800'>{item.client_name}</td>
                    <td className='px-3 py-2 whitespace-nowrap text-gray-800'>{item.type_label}</td>
                    <td className='px-3 py-2 whitespace-nowrap text-gray-800'>
                      {moment(item.date).format('DD/MM/YYYY')}
                    </td>
                    <td className='px-3 py-2 whitespace-nowrap text-gray-800'>
                      <NumericFormat
                        value={item.amount}
                        displayType='text'
                        thousandSeparator='.'
                        decimalSeparator=','
                        suffix={' €'}
                        decimalScale={2}
                      />
                    </td>
                    <td className='px-3 py-2 whitespace-nowrap text-gray-800'>
                      {item.client_internal_reference}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {pagination.total_pages > 1 && (
          <Pagination
            currentPage={pagination.current_page}
            totalPages={pagination.total_pages}
            onPageChange={page => updateParam('page', String(page))}
          />
        )}
      </div>
    </MainLayout>
  );
};

export default RecoveredAmountList;
